import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export interface Manufacturer {
  id: number; manufacturerCode: string; name: string; contactPerson: string | null; email: string | null;
  mobile: string | null; address: string | null; gstNumber: string | null; panNumber: string | null;
  bankName: string | null; bankAccount: string | null; bankIfsc: string | null; commercialTerms: string | null;
  paymentCreditDays: number; status: string; remarks: string | null;
}
export interface ManufacturingOrder {
  id: number; moCode: string; manufacturerId: number; productId: number | null; productName: string | null;
  quantity: number; rate: number; totalAmount: number; expectedDeliveryDate: string | null;
  dailyCommitmentQty: number; producedQty: number; status: string; notes: string | null;
}
export interface ManufacturerInvoice {
  id: number; manufacturerId: number; moId: number | null; invoiceNumber: string; invoiceAmount: number;
  invoiceReceivedDate: string; creditDays: number; dueDate: string | null; paidAmount: number;
  outstanding: number; status: string; paymentDate: string | null; remarks: string | null;
}

export interface ManufacturerDashboard {
  manufacturers: { total: number; active: number; inactive: number };
  orders: { total: number; pendingDeliveries: number };
  stock: { orderedQty: number; producedQty: number; pendingQty: number };
  outstandingPayable: number;
}
export interface PaymentReconciliation {
  totals: { invoices: number; totalInvoiced: number; totalPaid: number; totalOutstanding: number; overdue: number };
  ageing: Record<string, number>;
}

type ListParams = Record<string, unknown>;

export const sourcingApi = {
  // Manufacturers
  async dashboard() { return (await apiClient.get<ApiSuccess<ManufacturerDashboard>>('/manufacturers/dashboard')).data.data; },
  async listManufacturers(params?: ListParams): Promise<Paginated<Manufacturer>> { const { data } = await apiClient.get<ApiSuccess<Manufacturer[]>>('/manufacturers', { params }); return { data: data.data, meta: data.meta as PaginationMeta }; },
  async getManufacturer(id: number) { return (await apiClient.get<ApiSuccess<Manufacturer & { products: { productId: number; productName: string | null }[]; recentOrders: ManufacturingOrder[]; outstandingPayable: number }>>(`/manufacturers/${id}`)).data.data; },
  async onboard(body: Record<string, unknown>) { return (await apiClient.post<ApiSuccess<Manufacturer>>('/manufacturers', body)).data.data; },
  async updateManufacturer(id: number, body: Record<string, unknown>) { return (await apiClient.put<ApiSuccess<Manufacturer>>(`/manufacturers/${id}`, body)).data.data; },
  async removeManufacturer(id: number) { await apiClient.delete(`/manufacturers/${id}`); },

  // Manufacturing orders
  async listOrders(params?: ListParams): Promise<Paginated<ManufacturingOrder>> { const { data } = await apiClient.get<ApiSuccess<ManufacturingOrder[]>>('/manufacturing-orders', { params }); return { data: data.data, meta: data.meta as PaginationMeta }; },
  async getOrder(id: number) { return (await apiClient.get<ApiSuccess<ManufacturingOrder & { dailyLogs: { id: number; logDate: string; committedQty: number; producedQty: number; handoverQty: number; remarks: string | null }[] }>>(`/manufacturing-orders/${id}`)).data.data; },
  async createOrder(body: Record<string, unknown>) { return (await apiClient.post<ApiSuccess<ManufacturingOrder>>('/manufacturing-orders', body)).data.data; },
  async addDailyLog(id: number, body: Record<string, unknown>) { return (await apiClient.post(`/manufacturing-orders/${id}/daily-log`, body)).data; },
  async completeOrder(id: number) { return (await apiClient.post(`/manufacturing-orders/${id}/complete`, {})).data; },
  async cancelOrder(id: number) { return (await apiClient.post(`/manufacturing-orders/${id}/cancel`, {})).data; },

  // Manufacturer payments
  async reconciliation() { return (await apiClient.get<ApiSuccess<PaymentReconciliation>>('/manufacturer-payments/reconciliation')).data.data; },
  async listInvoices(params?: ListParams): Promise<Paginated<ManufacturerInvoice>> { const { data } = await apiClient.get<ApiSuccess<ManufacturerInvoice[]>>('/manufacturer-payments', { params }); return { data: data.data, meta: data.meta as PaginationMeta }; },
  async createInvoice(body: Record<string, unknown>) { return (await apiClient.post<ApiSuccess<ManufacturerInvoice>>('/manufacturer-payments', body)).data.data; },
  async recordPayment(id: number, body: Record<string, unknown>) { return (await apiClient.post<ApiSuccess<ManufacturerInvoice>>(`/manufacturer-payments/${id}/pay`, body)).data.data; },
};
